import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { SEOHead } from '@/components/SEOHead';
import { servicePillars } from '@/data/servicePillars';

const solutions = [
  {
    title: 'E-commerce Growth Support',
    desc: 'End-to-end support for online sellers across marketplaces and your own store — from product listings and catalogue management to customer support, inventory, digital marketing, and performance reporting.',
    pillars: ['E-commerce & Marketplace', 'Customer Experience', 'Digital Solutions'],
  },
  {
    title: 'Digital Business Support',
    desc: 'A complete digital growth programme combining website development, SEO, social media, content creation, paid advertising, and lead generation — everything you need to grow online.',
    pillars: ['Digital Solutions', 'Business Admin', 'Customer Experience'],
  },
  {
    title: 'Complete Business Support',
    desc: 'The full operational stack — administration, finance, HR, customer support, digital services, and e-commerce operations — delivered as a seamless, integrated service.',
    pillars: ['All Six Service Pillars'],
  },
];

export default function Services() {
  useEffect(() => { document.title = 'Our Services | NKP VENTURES — Business Operations & Digital Growth'; }, []);

  return (
    <>
      <SEOHead
        title="Business Operations, Digital Growth & E-commerce Services | NKP VENTURES"
        description="Explore NKP VENTURES's six service pillars: Business & Administrative Support, Customer Experience, Finance & Accounting, HR & Recruitment, Digital Solutions, and E-commerce & Marketplace Management."
        canonical={`${process.env.REACT_APP_BACKEND_URL || ''}/services`}
      />
      <div>

        {/* ── Hero ── */}
        <section className="relative pt-32 pb-28 overflow-hidden" style={{ background: '#0D1F30' }}>
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.018) 1px, transparent 0)',
            backgroundSize: '36px 36px'
          }} />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
              <div className="flex items-center gap-2.5 mb-8">
                <span className="w-5 h-px bg-[#D4AF37]" />
                <span className="overline-label text-[#D4AF37]">Our Services</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
                <div>
                  <h1 className="font-serif font-bold text-white leading-[1.05] mb-6" style={{ fontSize: 'clamp(2.6rem, 6vw, 4.2rem)' }}>
                    Six pillars.
                    <br />
                    <span style={{ color: '#D4AF37' }}>One partner.</span>
                  </h1>
                  <p className="text-white/55 text-lg leading-relaxed mb-10 max-w-lg">
                    From back-office operations to digital growth, NKP VENTURES provides the people, processes and expertise businesses need to operate efficiently, grow online and scale internationally.
                  </p>
                  <Link to="/contact"
                    className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0A1628] font-semibold px-7 py-3.5 rounded-full transition-all duration-200 text-sm group">
                    Schedule a Consultation
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                </div>

                {/* Pillar preview grid */}
                <div className="hidden lg:grid grid-cols-2 gap-2">
                  {servicePillars.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <motion.div key={p.id}
                        initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.15 + i * 0.05, duration: 0.5 }}
                        className="flex items-center gap-3 px-4 py-3 border border-white/8 rounded-xl hover:border-white/16 hover:bg-white/4 transition-all duration-200">
                        <Icon className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        <span className="text-white/65 text-xs font-sans">{p.shortTitle}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Six Service Pillars ── */}
        <section className="py-24 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="flex items-end justify-between mb-16">
                <div>
                  <p className="overline-label text-[#0E5A57] mb-4">Complete Service Portfolio</p>
                  <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#153E5C] leading-tight max-w-xl">
                    What we manage<br />for your business
                  </h2>
                </div>
                <p className="hidden lg:block text-sm text-[#6B7280] max-w-xs text-right leading-relaxed">
                  Each pillar is delivered to the same standard of professionalism, process discipline, and quality.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {servicePillars.map((pillar, i) => {
                const Icon = pillar.icon;
                return (
                  <Reveal key={pillar.id} delay={i * 0.09}>
                    <Link
                      to={`/services/${pillar.slug}`}
                      data-testid={`pillar-card-${pillar.slug}`}
                      className="group flex flex-col p-7 bg-[#F7FAFC] border border-[#E5E7EB] rounded-2xl hover:bg-white hover:border-[#0E5A57]/25 hover:shadow-[0_4px_28px_rgba(14,90,87,0.09)] hover:-translate-y-1 transition-all duration-300 h-full"
                    >
                      {/* Number + Icon row */}
                      <div className="flex items-start justify-between mb-5">
                        <span className="font-mono text-[11px] font-medium text-[#D4AF37] tracking-widest">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <div className="w-11 h-11 bg-white border border-[#E5E7EB] rounded-xl flex items-center justify-center group-hover:bg-[#0E5A57] group-hover:border-[#0E5A57] transition-all duration-300">
                          <Icon className="w-5 h-5 text-[#0E5A57] group-hover:text-white transition-colors duration-300" />
                        </div>
                      </div>

                      {/* Title + tagline */}
                      <h2 className="font-serif text-lg font-bold text-[#153E5C] mb-2 group-hover:text-[#0E5A57] transition-colors duration-200 leading-snug">
                        {pillar.title}
                      </h2>
                      <p className="text-sm text-[#6B7280] leading-relaxed font-sans mb-5">
                        {pillar.tagline}
                      </p>

                      {/* Service list preview */}
                      <ul className="space-y-1.5 mb-6 flex-grow">
                        {pillar.services.slice(0, 4).map(s => (
                          <li key={s} className="flex items-center gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-[#0E5A57]/50 flex-shrink-0" />
                            <span className="text-xs text-[#6B7280] font-sans">{s}</span>
                          </li>
                        ))}
                        {pillar.services.length > 4 && (
                          <li className="flex items-center gap-2">
                            <span className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="text-xs text-[#9CA3AF] font-sans">+{pillar.services.length - 4} more services</span>
                          </li>
                        )}
                      </ul>

                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E5A57] group-hover:gap-2.5 transition-all duration-200">
                        Explore service pillar <ArrowRight className="w-3 h-3" />
                      </span>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── E-commerce emphasis ── */}
        <section className="py-24 bg-[#0D1F30] overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <div className="flex items-center gap-2.5 mb-6">
                    <span className="w-5 h-px bg-[#D4AF37]" />
                    <span className="overline-label text-[#D4AF37]">E-commerce Expertise</span>
                  </div>
                  <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
                    Expert support for online sellers across every major marketplace
                  </h2>
                  <p className="text-white/55 text-base font-sans leading-relaxed mb-8">
                    Whether you sell on Amazon, eBay, Walmart, Etsy, Shopify, or WooCommerce — NKP VENTURES provides the operational expertise to manage your listings, orders, inventory, customer support, and account health professionally and consistently.
                  </p>
                  <Link to="/services/ecommerce-marketplace-management"
                    data-testid="ecommerce-pillar-cta"
                    className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0A1628] font-semibold px-6 py-3 rounded-full transition-all duration-200 text-sm group">
                    Explore E-commerce Services
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {['Amazon', 'eBay', 'Walmart', 'Etsy', 'Shopify', 'WooCommerce'].map((mp, i) => (
                    <motion.div key={mp}
                      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                      className="flex items-center justify-center px-4 py-4 bg-white/6 border border-white/10 rounded-xl hover:border-white/20 hover:bg-white/10 transition-all duration-200">
                      <span className="text-white/80 text-sm font-semibold font-sans">{mp}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="text-xs text-white/30 mt-8 font-sans">
                NKP VENTURES provides operational support for sellers on these platforms. We are not an officially authorised partner of Amazon, eBay, Walmart, Etsy, Shopify, or WooCommerce.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ── Service Solutions / Packages ── */}
        <section className="py-24 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <p className="overline-label text-[#0E5A57] mb-4">Service Solutions</p>
              <div className="flex items-end justify-between mb-14">
                <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#153E5C] leading-tight max-w-xl">
                  Services combined<br />into complete solutions
                </h2>
                <p className="hidden lg:block text-sm text-[#6B7280] max-w-xs text-right leading-relaxed">
                  Our services can be combined into integrated solutions that address your complete operational and growth needs.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {solutions.map((sol, i) => (
                <Reveal key={sol.title} delay={i * 0.07}>
                  <div className="group flex flex-col p-8 border border-[#E5E7EB] rounded-2xl bg-[#F7FAFC] hover:bg-white hover:border-[#0E5A57]/20 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-[#0E5A57] to-[#D4AF37] rounded mb-6" />
                    <h3 className="font-serif text-xl font-bold text-[#153E5C] mb-3 leading-snug">
                      {sol.title}
                    </h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed font-sans mb-6 flex-grow">
                      {sol.desc}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {sol.pillars.map(tag => (
                        <span key={tag} className="text-xs bg-[#0E5A57]/8 text-[#0E5A57] font-medium px-2.5 py-1 rounded-full font-sans">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <p className="text-xs text-[#9CA3AF] mt-6 font-sans text-center">
                These are example solution combinations, not fixed-price packages. Speak to our team to discuss your specific requirements.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ── Commitment strip ── */}
        <section className="py-16 bg-[#F7FAFC] border-t border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { title: 'Consistent Quality', desc: 'Every service pillar is delivered to the same standard — no variation based on task type or volume.' },
                  { title: 'Documented Processes', desc: 'We operate from clear SOPs and workflows. Nothing is improvised or inconsistently applied.' },
                  { title: 'International Standards', desc: 'Professional communication and operational standards across every channel, every interaction.' },
                ].map(item => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-px bg-gradient-to-b from-[#0E5A57] to-[#D4AF37] rounded-full" style={{ minHeight: '60px' }} />
                    <div>
                      <h3 className="font-serif text-base font-bold text-[#153E5C] mb-2">{item.title}</h3>
                      <p className="text-sm text-[#6B7280] leading-relaxed font-sans">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-28 bg-[#0E5A57]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <p className="overline-label text-teal-300 mb-4">Not Sure Where to Start?</p>
                  <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                    Talk to our team about<br />your specific needs.
                  </h2>
                  <p className="text-white/60 text-base font-sans leading-relaxed max-w-lg">
                    We'll help you identify the right combination of services for your operational and growth challenges — no obligation, no pressure.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                  <Link to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                    Schedule a Consultation
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                  </Link>
                  <Link to="/about"
                    className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                    Learn About Us
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

      </div>
    </>
  );
}
